import { useState, useEffect, useMemo } from 'react';
import { AlertTriangle, Search } from 'lucide-react';
import axios from 'axios';
import { QuickPreorderModal } from './QuickPreorderModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Ruptures & stock bas non encore en précommande → mise en précommande rapide.
export const LowStockPreorderPanel = ({ onChanged }) => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [modal, setModal] = useState(null);

  const load = () => axios.get(`${API_URL}/api/products`).then(r => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const candidates = useMemo(() => {
    const q = search.toLowerCase();
    return products
      .filter(p => !p.preorder_enabled && (p.stock || 0) <= (p.min_stock ?? 5))
      .filter(p => !q || `${p.brand} ${p.name}`.toLowerCase().includes(q))
      .sort((a, b) => (a.stock || 0) - (b.stock || 0));
  }, [products, search]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const done = () => { setModal(null); setSelected(new Set()); load(); onChanged(); };

  return (
    <section className="mb-10" data-testid="low-stock-preorder-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
        <h2 className="text-white text-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-400" /> Ruptures & stock bas ({candidates.length})
        </h2>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher..."
              className="bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white text-sm pl-9 pr-3 py-2 outline-none w-56"
              data-testid="low-stock-search" />
          </div>
          {selected.size > 0 && (
            <button type="button" onClick={() => setModal(candidates.filter(p => selected.has(p.id)))}
              className="bg-[color:var(--color-primary)] text-black text-sm px-4 py-2 hover:bg-[color:var(--color-primary-hover)]"
              data-testid="low-stock-bulk-preorder-btn">
              Précommander la sélection ({selected.size})
            </button>
          )}
        </div>
      </div>
      {candidates.length === 0 ? (
        <p className="text-zinc-500 text-sm border border-white/10 p-4 text-center">Aucun produit en rupture ou stock bas hors précommande.</p>
      ) : (
        <ul className="border border-white/10 divide-y divide-white/5 max-h-80 overflow-y-auto" data-testid="low-stock-list">
          {candidates.map(p => (
            <li key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-white/5" data-testid={`low-stock-row-${p.id}`}>
              <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} className="accent-[color:var(--color-primary)]"
                data-testid={`low-stock-check-${p.id}`} />
              <div className="flex-1 min-w-0">
                <span className="text-zinc-500 text-[10px] uppercase tracking-widest mr-2">{p.brand}</span>
                <span className="text-white text-sm truncate">{p.name}</span>
              </div>
              <span className={`text-xs ${(p.stock || 0) <= 0 ? 'text-red-400' : 'text-orange-400'}`}>
                {(p.stock || 0) <= 0 ? 'Rupture' : `Stock ${p.stock}`}
              </span>
              <button type="button" onClick={() => setModal([p])}
                className="text-xs px-2.5 py-1 border border-[color:rgb(var(--primary-rgb)/0.40)] text-[color:var(--color-primary)] hover:bg-[color:var(--color-primary)] hover:text-black"
                data-testid={`low-stock-preorder-btn-${p.id}`}>
                Précommander
              </button>
            </li>
          ))}
        </ul>
      )}
      {modal && <QuickPreorderModal products={modal} onClose={() => setModal(null)} onDone={done} />}
    </section>
  );
};
